<script setup lang="ts">
import moment from 'moment-jalaali'
import { useField, useForm } from 'vee-validate'
import { ref, watch } from 'vue'
import * as yup from 'yup'
import {
  registerSettlementReceiptAction,
  uploadSettlementReceiptAction,
} from '@/api/functions/transactions/transaction-receipt-upload.action'
import { useMutationApi } from '@/composables/useApi'
import { useJalaaliDate } from '@/composables/useJalaaliDate.ts'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import TransactionReceiptUploader from '../partials/TransactionReceiptUploader.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  transaction: { type: Object },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'finish'])

const uploadedFiles = ref<any[]>([])
const toast = useToast()
const { toGregorian } = useJalaaliDate()

const schema = yup.object({
  trackingCode: yup.string().required('این فیلد الزامی است'),
  amount: yup.number().required('مقدار  الزامی است').typeError('این فیلد الزامی است'),
  status: yup.string().required(' وضعیت الزامی است'),
  day: yup.number().required('این فیلد الزامی است').typeError('این فیلد الزامی است'),
  month: yup.number().required('این فیلد الزامی است').typeError('این فیلد الزامی است'),
  year: yup.number().required('این فیلد الزامی است').typeError('این فیلد الزامی است'),
  minute: yup.number().required('این فیلد الزامی است').typeError('این فیلد الزامی است'),
  hour: yup.number().required('این فیلد الزامی است').typeError('این فیلد الزامی است'),
})

// Form setup
const { handleSubmit, errors, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    trackingCode: '',
    amount: null as number | null,
    status: '',
    day: null as number | null,
    month: null as number | null,
    year: null as number | null,
    minute: null as number | null,
    hour: null as number | null,
  },
})

// Fields
const { value: trackingCode } = useField('trackingCode')
const { value: amount } = useField('amount')
const { value: status } = useField('status')
const { value: day } = useField('day')
const { value: month } = useField('month')
const { value: year } = useField('year')
const { value: minute } = useField('minute')
const { value: hour } = useField('hour')

watch(
  () => props.transaction,
  (transaction) => {
    if (transaction) {
      const today = moment()
      setValues({
        trackingCode: transaction?.code + 'Settlement' || '',
        amount: Number(transaction?.sellerReceivableRialAmount),
        status:
          transaction?.status === 'SETTLE_WITH_SELLER_PENDING' || transaction?.status === 'FINISHED'
            ? 'SUCCESSFUL'
            : 'FAILED',
        day: today.jDate(),
        month: today.jMonth() + 1,
        year: today.jYear(),
        hour: today.hour(),
        minute: today.minute(),
      })
    }
  },
  { immediate: true }
)

const uploadMutation = useMutationApi({
  mutationFn: async () => {
    // if (!uploadedFiles.value || !uploadedFiles.value.length) {
    //   throw new Error('هیچ فایلی انتخاب نشده است')
    // }

    const images = uploadedFiles.value.map((f) => f.file)
    const uuidToUse = props?.transaction?.uuid

    if (!uuidToUse) {
      throw new Error('شناسه تراکنش پیدا نشد')
    }

    //  request 1 : آپلود رسید
    await uploadSettlementReceiptAction(uuidToUse, { images })

    //  request 2: ثبت اطلاعات تسویه
    const date = toGregorian(
      Number(year.value),
      Number(month.value),
      Number(day.value),
      Number(hour.value),
      Number(minute.value)
    )

    return registerSettlementReceiptAction(uuidToUse, {
      amount: Number(amount.value),
      date,
      status: String(status.value),
      tracking_id: String(trackingCode.value),
    })
  },

  onSuccess: () => {
    toast.success('تسویه با فروشنده با موفقیت ارسال شد')

    emit('confirm')
    emit('finish') // TODO: What is the difference between Confirm and Finish? Why do we need both?
    closeModal()
  },

  onError: (err: Error) => {
    toast.error(getErrorMessage(err) || 'خطا در ارسال رسید')
  },
})

const onSubmit = handleSubmit(() => {
  if (uploadMutation.isPending.value) return
  uploadMutation.mutate()
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
}
</script>

<template>
    <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
        background="#FFF" :closable="true" title="تسویه با فروشنده" closeDirection="left" class="update-request">
        <form @submit.prevent="onSubmit" class="flex flex-col h-[800px]">
            <div class="flex-1  px-4 py-6 pb-9 space-y-3">

                <Input v-model="trackingCode" name="trackingCode" :error="errors.trackingCode" label="کد پیگیری تسویه"
                    type="text" />

                <Input v-model="amount" name="amount" :error="errors.amount" label="مبلغ تراکنش" type="number" />

                <Select variant="checkbox" v-model="status" :error="errors.status" label="وضعیت تراکنش" :options="[
                    { label: 'موفق', value: 'SUCCESSFUL' },
                    { label: 'ناموفق', value: 'FAILED' }
                ]" />

                <div>
                    <span class="text-[13px] font-medium text-[#1b325a]">تاریخ تراکنش</span>
                    <div class="flex items-center gap-2 mt-3">
                        <Input v-model="day" name="day" :error="errors.day" label="روز" type="number"
                            class="w-[100px]!" />
                        <span class="-mt-5">/</span>
                        <Input v-model="month" name="month" :error="errors.month" label="ماه" type="number"
                            class="w-[100px]!" />
                        <span class="-mt-5">/</span>
                        <Input v-model="year" name="year" :error="errors.year" label="سال" type="number"
                            class="w-[100px]!" />
                    </div>
                </div>

                <div class="">
                    <span class="text-[13px] font-medium text-[#1b325a]">ساعت تراکنش</span>
                    <div class="flex items-center gap-2 mt-3">
                        <Input v-model="minute" name="minute" :error="errors.minute" label="دقیقه" type="number"
                            class="w-[100px]!" />
                        <span class="-mt-5">/</span>
                        <Input v-model="hour" name="hour" :error="errors.hour" label="ساعت" type="number"
                            class="w-[100px]!" />
                    </div>
                </div>

                <div class="">
                    <TransactionReceiptUploader class="mt-4" @update:files="uploadedFiles = $event" />
                </div>
            </div>

            <div
                class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2  border-gray-200">
                <Button type="submit" label="تایید تسویه با فروشنده" :isAdmin="true" class="w-9/12  h-12 rounded-xl" />
                <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
                    class="w-4/12  h-12 rounded-xl border-2" />
            </div>
        </form>
    </Modal>
</template>
